import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useTranslatedMap } from '@/contexts/LanguageContext';
import { AvailableSpaceCard } from './AvailableSpaceCard';
import { SpotlightCreative } from './SpotlightCreative';

/**
 * SpotlightSlot — renders promotional placements for a named slot
 * (Session 36, Phase 1). Subtle, on-brand "Featured" cards. Fires impression
 * tracking on mount and click tracking on CTA. Internal CTAs navigate in-app;
 * external CTAs open a new tab (rel=noopener) and are tracked as clicks.
 *
 * The creative markup lives in SpotlightCreative so the admin preview shows the real thing.
 *
 * Props:
 *   slot      — slot key (e.g. "marketplace_banner")
 *   variant   — "banner" | "card" | "flank" | "rail"
 *   theme     — "dark" (default, for dark surfaces) | "light" (Bookings portal)
 *   pick      — optional index to render a single placement (for interleaving)
 *   className — extra classes
 */
export function SpotlightSlot({ slot, variant = 'banner', theme = 'dark', pick = null, className = '' }) {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const [placements, setPlacements] = useState([]);
  const [availableSpace, setAvailableSpace] = useState(null);
  const trackedRef = useRef(new Set());
  // Translate ad creative text (title/body/CTA/badge) to the active language
  const tx = useTranslatedMap([
    ...placements.flatMap((p) => [p.title, p.body, p.cta_label, p.label]),
    'Featured', 'Learn more',
  ]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/spotlights?slot=${slot}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setPlacements(res.data.placements || []);
        setAvailableSpace(res.data.available_space || null);
      })
      .catch(() => setPlacements([]));
  }, [slot, token]);

  useEffect(() => {
    placements.forEach((p) => {
      if (trackedRef.current.has(p.id)) return;
      trackedRef.current.add(p.id);
      axios.post(`${API}/spotlights/${p.id}/impression`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    });
  }, [placements, token]);

  const handleClick = (p) => {
    axios.post(`${API}/spotlights/${p.id}/click`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    if (p.cta_type === 'external' && p.external_url) {
      window.open(p.external_url, '_blank', 'noopener,noreferrer');
    } else if (p.link_path) {
      navigate(p.link_path);
    }
  };

  if (!placements.length) {
    // Nothing sold here. If he has switched invitations on for this space, practitioners and
    // vendors get a quiet "available" card instead of a gap.
    if (availableSpace) {
      return <AvailableSpaceCard space={availableSpace} variant={variant} theme={theme} className={className} />;
    }
    return null;
  }
  const items = pick !== null ? [placements[pick % placements.length]] : placements;

  if (variant === 'banner') {
    return (
      <SpotlightCreative p={items[0]} variant="banner" theme={theme} tx={tx}
        className={className} testId={`spotlight-${slot}`}
        onClick={() => handleClick(items[0])} />
    );
  }

  if (variant === 'card' || variant === 'flank') {
    return (
      <>
        {items.map((p) => (
          <SpotlightCreative key={p.id} p={p} variant={variant} theme={theme} tx={tx}
            className={className} testId={`spotlight-${slot}`}
            onClick={() => handleClick(p)} />
        ))}
      </>
    );
  }

  return (
    <div className={`flex gap-3 overflow-x-auto pb-2 ${className}`} data-testid={`spotlight-${slot}`} style={{ scrollbarWidth: 'none' }}>
      {items.map((p) => (
        <SpotlightCreative key={p.id} p={p} variant="rail" theme={theme} tx={tx}
          onClick={() => handleClick(p)} />
      ))}
    </div>
  );
}
